import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LoadingSkeleton } from "@/components/shared/States";
import { ScorePill, Avatar } from "@/components/shared/Badges";
import { ArrowLeft, Sparkles, Loader2, Building2 } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => { const { data } = await api.get(`/companies/${id}`); setC(data); setIntel(data.ai_brief || null); };
  useEffect(() => { load(); }, [id]);
  const runIntel = async () => { setLoading(true); try { const { data } = await api.post(`/companies/${id}/ai-intel`); setIntel(data); toast.success("Account intelligence ready"); } catch { toast.error("Failed"); } finally { setLoading(false); } };

  if (!c) return <LoadingSkeleton />;
  const pipeline = (c.deals || []).reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/app/companies")} className="mb-3 text-slate-500"><ArrowLeft className="h-4 w-4 mr-1" />Companies</Button>
      <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-4">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl aurelo-gradient text-white flex items-center justify-center text-2xl font-bold">{c.name[0]}</div>
          <div className="flex-1"><h1 className="text-xl font-bold text-slate-900">{c.name}</h1><p className="text-sm text-slate-500">{c.industry} · {c.domain}</p></div>
          <div className="text-right"><div className="text-xs text-slate-400">Account Score</div><ScorePill score={c.account_score} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {[["Revenue", c.revenue], ["Employees", c.employees], ["Active Deals", (c.deals || []).length], ["Total Pipeline", fmtMoney(pipeline)]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] text-slate-400">{l}</div><div className="font-semibold text-slate-900">{v}</div></div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Tabs defaultValue="contacts">
            <TabsList><TabsTrigger value="contacts">Contacts ({(c.contacts || []).length})</TabsTrigger><TabsTrigger value="deals">Deals ({(c.deals || []).length})</TabsTrigger></TabsList>
            <TabsContent value="contacts" className="mt-3 space-y-2">
              {(c.contacts || []).length === 0 && <div className="text-sm text-slate-400 p-4">No contacts.</div>}
              {(c.contacts || []).map((ct) => (
                <div key={ct.id} onClick={() => navigate(`/app/contacts/${ct.id}`)} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"><Avatar name={`${ct.first_name} ${ct.last_name}`} src={ct.avatar} size={32} /><div><div className="text-sm font-medium">{ct.first_name} {ct.last_name}</div><div className="text-xs text-slate-400">{ct.title}</div></div><div className="ml-auto"><ScorePill score={ct.lead_score} /></div></div>
              ))}
            </TabsContent>
            <TabsContent value="deals" className="mt-3 space-y-2">
              {(c.deals || []).map((d) => (
                <div key={d.id} onClick={() => navigate(`/app/deals/${d.id}`)} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"><div className="flex-1"><div className="text-sm font-medium">{d.name}</div><div className="text-xs text-slate-400">{d.stage}</div></div><div className="text-sm font-bold tabular-nums">{fmtMoney(d.value)}</div></div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-[#7C3AED] flex items-center gap-1.5 mb-3"><Sparkles className="h-4 w-4" />Account Intelligence</h3>
          {intel ? (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-slate-600">{intel.overview}</p>
              {intel.buying_signals?.length > 0 && <div><div className="text-xs font-semibold text-slate-700 mb-1">Buying Signals</div><ul className="space-y-1">{intel.buying_signals.slice(0, 3).map((s, i) => <li key={i} className="text-xs text-slate-500 flex gap-1.5"><span className="text-emerald-500">•</span>{s}</li>)}</ul></div>}
              {intel.suggested_strategy && <div className="rounded-xl bg-violet-50 p-3 text-xs text-violet-800"><b>Strategy:</b> {intel.suggested_strategy}</div>}
            </div>
          ) : (
            <div className="text-center py-3"><p className="text-sm text-slate-500 mb-3">Generate an AI account brief with buying signals, stakeholders & strategy.</p><Button data-testid="generate-account-intel" size="sm" className="aurelo-gradient text-white w-full" onClick={runIntel} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Account Intelligence"}</Button></div>
          )}
        </div>
      </div>
    </div>
  );
}
